import smCrypto from 'sm-crypto'

const sm4 = smCrypto.sm4

/** localStorage 中持久化 SM4 对称密钥的键名（首次使用时随机生成并写入） */
export const LOCAL_CRYPTO_KEY_STORAGE = '__yj_local_sm4_key__'

/** 密文包版本号；变更 {@link LocalCipherEnvelope} 结构时递增 */
export const LOCAL_CIPHER_VERSION = 1

/** SM4-CBC 密文包（hex 编码） */
export interface LocalCipherEnvelope {
  v: number
  iv: string
  data: string
}

const SM4_KEY_BYTES = 16
const SM4_IV_BYTES = 16

/** 进程内兜底密钥（localStorage 不可用时） */
let inMemoryFallbackKey: string | null = null

function randomHex(byteLength: number): string {
  const bytes = new Uint8Array(byteLength)
  crypto.getRandomValues(bytes)
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
}

function isValidSm4KeyHex(key: string): boolean {
  return /^[0-9a-fA-F]{32}$/.test(key)
}

function isValidCipherEnvelope(value: unknown): value is LocalCipherEnvelope {
  if (value == null || typeof value !== 'object') return false
  const o = value as LocalCipherEnvelope
  return (
    o.v === LOCAL_CIPHER_VERSION &&
    typeof o.iv === 'string' &&
    /^[0-9a-fA-F]{32}$/.test(o.iv) &&
    typeof o.data === 'string' &&
    o.data.length > 0
  )
}

/**
 * 生成 SM4 对称密钥（128 bit，32 位十六进制字符串）。
 * 每次调用均产生新密钥，不会自动写入 localStorage。
 */
export function generateLocalCryptoKey(): string {
  return randomHex(SM4_KEY_BYTES)
}

/**
 * 读取或创建本机 SM4 密钥：优先 localStorage，不可用时退化为当前 Tab 内存密钥。
 */
export function getOrCreateLocalCryptoKey(): string {
  try {
    const existing = localStorage.getItem(LOCAL_CRYPTO_KEY_STORAGE)
    if (existing && isValidSm4KeyHex(existing)) return existing
    const key = generateLocalCryptoKey()
    localStorage.setItem(LOCAL_CRYPTO_KEY_STORAGE, key)
    inMemoryFallbackKey = null
    return key
  } catch {
    if (inMemoryFallbackKey && isValidSm4KeyHex(inMemoryFallbackKey)) {
      return inMemoryFallbackKey
    }
    inMemoryFallbackKey = generateLocalCryptoKey()
    return inMemoryFallbackKey
  }
}

/**
 * 清除本机 SM4 密钥。已加密数据将无法解密，除非调用方自行保存了密钥副本。
 */
export function resetLocalCryptoKey(): void {
  inMemoryFallbackKey = null
  try {
    localStorage.removeItem(LOCAL_CRYPTO_KEY_STORAGE)
  } catch {
    // ignore
  }
}

/**
 * SM4-CBC 加密本地明文，返回 JSON 密文包字符串。
 *
 * @param plainText 明文字符串
 * @param key 可选；默认 {@link getOrCreateLocalCryptoKey}
 */
export function localEncrypt(plainText: string, key?: string): string {
  const k = key ?? getOrCreateLocalCryptoKey()
  const iv = randomHex(SM4_IV_BYTES)
  const data = sm4.encrypt(plainText, k, { mode: 'cbc', iv, padding: 'pkcs#7' })
  const envelope: LocalCipherEnvelope = { v: LOCAL_CIPHER_VERSION, iv, data }
  return JSON.stringify(envelope)
}

/**
 * 解密 {@link localEncrypt} 产出的密文包；失败返回 `null`。
 *
 * @param cipherPayload JSON 密文包或已解析对象
 * @param key 可选；默认 {@link getOrCreateLocalCryptoKey}
 */
export function localDecrypt(
  cipherPayload: string | LocalCipherEnvelope,
  key?: string,
): string | null {
  try {
    const envelope =
      typeof cipherPayload === 'string' ? (JSON.parse(cipherPayload) as unknown) : cipherPayload
    if (!isValidCipherEnvelope(envelope)) return null
    const k = key ?? getOrCreateLocalCryptoKey()
    return sm4.decrypt(envelope.data, k, {
      mode: 'cbc',
      iv: envelope.iv,
      padding: 'pkcs#7',
    })
  } catch {
    return null
  }
}

/** 将对象序列化为 JSON 后加密 */
export function localEncryptJson(value: unknown, key?: string): string {
  return localEncrypt(JSON.stringify(value), key)
}

/** 解密 JSON 密文包并反序列化；失败返回 `null` */
export function localDecryptJson<T = unknown>(
  cipherPayload: string | LocalCipherEnvelope,
  key?: string,
): T | null {
  const plain = localDecrypt(cipherPayload, key)
  if (plain == null) return null
  try {
    return JSON.parse(plain) as T
  } catch {
    return null
  }
}

/** 加密字符串并写入 localStorage */
export function setEncryptedLocalItem(storageKey: string, plainText: string, key?: string): void {
  try {
    localStorage.setItem(storageKey, localEncrypt(plainText, key))
  } catch {
    // 配额或隐私模式失败时忽略
  }
}

/** 读取 localStorage 中的加密字符串并解密 */
export function getEncryptedLocalItem(storageKey: string, key?: string): string | null {
  try {
    const raw = localStorage.getItem(storageKey)
    if (raw == null || raw === '') return null
    return localDecrypt(raw, key)
  } catch {
    return null
  }
}

/** 加密 JSON 并写入 localStorage */
export function setEncryptedLocalJson(storageKey: string, value: unknown, key?: string): void {
  try {
    if (value == null) {
      localStorage.removeItem(storageKey)
      return
    }
    localStorage.setItem(storageKey, localEncryptJson(value, key))
  } catch {
    // ignore
  }
}

/** 读取 localStorage 中的加密 JSON 并解密 */
export function getEncryptedLocalJson<T = unknown>(storageKey: string, key?: string): T | null {
  try {
    const raw = localStorage.getItem(storageKey)
    if (raw == null || raw === '') return null
    return localDecryptJson<T>(raw, key)
  } catch {
    return null
  }
}

/** 删除 localStorage 中指定键 */
export function removeEncryptedLocalItem(storageKey: string): void {
  try {
    localStorage.removeItem(storageKey)
  } catch {
    // ignore
  }
}
